import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import { ToolsListing } from '../../components/custom';
import { LayoutBase } from '../../layouts';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IProfessionals, ProfessionalsServices } from '../../services/api/professionals/ProfessionalsServices';
import { useNavigate, useParams } from 'react-router-dom';
import { Delete, Edit, ExpandMore } from '@mui/icons-material';
import { Loadin } from '../../components/shared';
import { AppSnackbarAlert } from '../../components/shared/AppSnackbarAlert';
import { ConfirmationDialog } from '../../components/shared';
import { useAuthContext } from '../../context';

export const ListProfessionals: React.FC = () => {
    const { id = 'nova' } = useParams<'id'>();
    const uuid = id.replace('dam=', '');
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<IProfessionals[]>([]);
    const navigate = useNavigate();
    const { userData } = useAuthContext();

    const [alertInfo, setAlertInfo] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'warning' | 'info';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [professionalToDelete, setProfessionalToDelete] = useState<string | undefined>(undefined);

    const handleCloseAlert = () => {
        setAlertInfo({ ...alertInfo, open: false });
    };

    const pagePermissionsString: string | undefined = useMemo(() => {
        return userData?.nivel_acesso?.informacoes?.organograma;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    useEffect(() => {
        setIsLoading(true);
        ProfessionalsServices.getList(uuid, 'uuid')
            .then((result) => {
                if (result instanceof Error) {
                    setAlertInfo({
                        open: true,
                        message: `Erro ao carregar profissionais: ${result.message}`,
                        severity: 'error'
                    });
                    setData([]);
                } else {
                    let dataArray = Object.values(result);
                    dataArray = dataArray.filter(item => item.status === true);
                    setData(dataArray);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [uuid]);

    if (isLoading) {
        return (
            <Loadin />
        );
    }

    const handleClickDelete = (professionalUuid: string | undefined) => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({
                open: true,
                message: 'Você não tem permissão para remover profissionais.',
                severity: 'error'
            });
            return;
        }

        if (!professionalUuid) return;

        const professional = data.find(p => p.uuid === professionalUuid);
        if (!professional) {
            setAlertInfo({
                open: true,
                message: 'Profissional não encontrado para exclusão.',
                severity: 'error'
            });
            return;
        }

        if (professional.status === false) {
            setProfessionalToDelete(professionalUuid);
            setOpenConfirmDialog(true);
            return;
        }
        const hasActiveSubordinates = data.some(p => p.id_superior === professionalUuid && p.status === true);

        if (hasActiveSubordinates) {
            setAlertInfo({
                open: true,
                message: 'Este profissional não pode ser deletado pois é um superior de outros profissionais ativos. Altere o status para inativo para poder deletá-lo.',
                severity: 'warning'
            });
            return;
        }
        setProfessionalToDelete(professionalUuid);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        setOpenConfirmDialog(false);
        if (professionalToDelete) {
            ProfessionalsServices.delet(professionalToDelete)
                .then(result => {
                    if (result instanceof Error) {
                        setAlertInfo({
                            open: true,
                            message: `Erro ao apagar o profissional: ${result.message}`,
                            severity: 'error'
                        });
                    } else {
                        setAlertInfo({
                            open: true,
                            message: 'Profissional apagado com sucesso!',
                            severity: 'success'
                        });
                        setData(prevData => prevData.filter(item => item.uuid !== professionalToDelete));
                    }
                })
                .catch(error => {
                    setAlertInfo({
                        open: true,
                        message: `Erro inesperado ao apagar: ${error.message}`,
                        severity: 'error'
                    });
                })
                .finally(() => {
                    setProfessionalToDelete(undefined);
                });
        }
    };

    const handleCancelDelete = () => {
        setOpenConfirmDialog(false);
        setProfessionalToDelete(undefined);
    };

    return (
        <LayoutBase
            title='Lista de Profissionais'
            tollBar={
                <ToolsListing
                    onClickNew={() => navigate('./new')}
                    showNewButton={hasActionPermission('add')}
                    showBackButton={true}
                />
            }
        >
            <Box width={'100%'} gap={2}>
                {data.length === 0 && !isLoading ? (
                    <Typography variant="subtitle1" textAlign="center" marginTop={4}>
                        Nenhum profissional encontrado.
                    </Typography>
                ) : (
                    data.map((item, index) => (
                        <Box key={index} marginBottom={2}>
                            <Accordion variant='outlined' square={true} sx={{ borderRadius: '30px', '&:before': { display: 'none', } }} disableGutters>
                                <AccordionSummary expandIcon={<ExpandMore />} >
                                    <Grid container alignItems="center">
                                        <Grid item xs={6}>
                                            <Typography variant='h5'>{item.nome}</Typography>
                                        </Grid>
                                        <Grid item xs={6} container justifyContent="flex-end">
                                            {hasActionPermission('edit') &&
                                                <IconButton onClick={(e) => { e.stopPropagation(); navigate(`./${item.uuid}`); }}><Edit /></IconButton>
                                            }
                                            {item.id_superior !== null && hasActionPermission('remove') &&
                                                <IconButton onClick={(e) => { e.stopPropagation(); handleClickDelete(item.uuid); }}><Delete color='error' /></IconButton>
                                            }
                                        </Grid>
                                    </Grid>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box padding={1} marginY={1} gap={2}>
                                        <Typography variant='subtitle2'>Formação</Typography>
                                        <Divider />
                                        <Typography variant='body1' marginBottom={2}>{item.curriculum || 'Não informado'}</Typography>
                                        <Typography variant='subtitle2'>Cargo</Typography>
                                        <Divider />
                                        <Typography variant='body1' marginBottom={2}>{item.role || 'Não informado'}</Typography>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    ))
                )}
            </Box>

            <AppSnackbarAlert
                severity={alertInfo.severity}
                message={alertInfo.message}
                open={alertInfo.open}
                onClose={handleCloseAlert}
            />

            <ConfirmationDialog
                isOpen={openConfirmDialog}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Confirmar Exclusão"
                message={'Tem certeza que deseja apagar este profissional? Esta ação não pode ser desfeita.'}
            />
        </LayoutBase>
    );
};